/**
 * **장식 조명 풀** (2026-09-26, Wayne: 「게임도 같은 최적화」).
 *
 * three 의 표준 재질은 픽셀마다 씬의 모든 조명을 돈다 — 멀리 있어 0 을 더하는 조명도 똑같이 센다.
 * 맥북 M2 Pro 실측(에디터): 조명 39개를 켜면 11fps, 끄면 52fps. draw call 이 아니라 **조명 수**가 병목이었다.
 * 그 중 가로등 16개·현관등·시신 곁 등처럼 **제자리에 붙박인 장식 조명**은 빛이 닿는 거리(distance)가 짧아서
 * 한 자리에서 실제로 기여하는 것은 몇 개뿐이다.
 *
 * 그래서 장식 조명(`markDecor`)은 씬에서 떼어 값만 기억하고, 고정 개수의 슬롯에 **지금 자리에서 가까운 것**만
 * 꽂는다. 슬롯 수가 늘 같아서 셰이더를 다시 굽지 않는다 — 방 조명 풀(world.ts cullLights)과 같은 원리다.
 * 빛이 닿는 거리 밖의 조명은 원래도 0 을 더했으므로, 슬롯이 모자라지 않는 한 화면은 같다.
 */
import * as THREE from 'three';

/**
 * **안 쓰는 점광 슬롯은 세기 0 으로 끄고 멀리(지하 10km) 세워 둔다** (2026-09-27). WebGPU 의 ClusteredLighting 은
 * 세기 0 인 조명도 닿는 칸에 넣고 픽셀마다 돈다 — 빈 슬롯 55개가 원점(조합 홀 자리)에 모여 있어 조합 홀이 56fps 였고,
 * 치우자 85fps. WebGL 에서는 자리가 상관없다(어차피 전부 돈다)
 */
export const PARKED = new THREE.Vector3(0, -10000, 0);
export function park(l: THREE.PointLight) {
  l.intensity = 0;
  l.position.copy(PARKED);
}

/** 이 조명은 붙박이 장식이다 — 풀이 거둬 간다(가로등·현관등 등) */
export function markDecor<T extends THREE.PointLight>(l: T): T {
  l.userData.decor = true;
  return l;
}

interface Src { pos: THREE.Vector3; color: THREE.Color; intensity: number; distance: number; decay: number }

export interface DecorPool {
  /** 이 자리(보통 카메라) 가까이의 장식 조명을 슬롯에 꽂는다. 싸다 — 몇 번 불러도 된다 */
  update(at: THREE.Vector3): void;
  /** 씬에 새로 생긴 장식 조명을 거둔다(에디터가 세계를 다시 지은 뒤). 처음 만들 때 한 번은 저절로 */
  adopt(): number;
  /** 지금 기억하는 장식 조명 수 */
  count(): number;
  slots: THREE.PointLight[];
}

export function createDecorPool(scene: THREE.Scene, slots = 4): DecorPool {
  const srcs: Src[] = [];
  const pool: THREE.PointLight[] = [];
  for (let i = 0; i < slots; i++) {
    const p = new THREE.PointLight('#ffffff', 0, 1, 2);
    p.name = 'decorSlot';
    scene.add(p);
    pool.push(p);
  }
  const adopt = () => {
    const found: THREE.PointLight[] = [];
    scene.traverse((o) => { const l = o as THREE.PointLight; if (l.isPointLight && l.userData.decor) found.push(l); });
    for (const l of found) {
      l.updateWorldMatrix(true, false);
      srcs.push({ pos: l.getWorldPosition(new THREE.Vector3()), color: l.color.clone(), intensity: l.intensity, distance: l.distance || 30, decay: l.decay });
      l.removeFromParent();
    }
    return found.length;
  };
  adopt();
  const update = (at: THREE.Vector3) => {
    // 빛이 닿는 거리 안에 있는 것만, 그 안쪽으로 깊은 것부터(= 가까운 것부터)
    const ranked = srcs
      .map((s) => ({ s, d: s.pos.distanceTo(at) - s.distance }))
      .filter((r) => r.d < 0)
      .sort((a, b) => a.d - b.d);
    for (let i = 0; i < pool.length; i++) {
      const p = pool[i], r = ranked[i];
      if (!r) { park(p); continue; }
      p.position.copy(r.s.pos); p.color.copy(r.s.color);
      p.intensity = r.s.intensity; p.distance = r.s.distance; p.decay = r.s.decay;
    }
  };
  return { update, adopt, count: () => srcs.length, slots: pool };
}

/**
 * WebGPU + ClusteredLighting(gpu.ts)이면 가로등·현관등을 **다 켜 둔다** — 점광 수가 픽셀 비용을 안 늘린다.
 * 풀과 같은 모양을 돌려준다. 슬롯 하나는 단서 사진 플래시가 빌려 쓴다(photo.ts)
 */
export function keepDecor(scene: THREE.Scene): DecorPool {
  let n = 0;
  scene.traverse((o) => { const l = o as THREE.PointLight; if (l.isPointLight && l.userData.decor) n++; });
  const spare = new THREE.PointLight('#ffffff', 0, 1, 2);
  spare.name = 'decorSlot';
  park(spare);
  scene.add(spare);
  return { update: () => {}, adopt: () => 0, count: () => n, slots: [spare] };
}
